"use client";

/**
 * AttachmentPreview — renders the list of files currently attached to the
 * prompt input (the chips shown above the textarea before sending).
 *
 * It reads from the {@link usePromptInputAttachments} context provided by
 * ``PromptInput`` / ``PromptInputProvider``.  Each attachment is shown as a
 * compact chip with an icon (or thumbnail for images) and a remove button.
 *
 * This component is a *presentational* sibling of the textarea — it has no
 * state of its own and re-renders automatically when the attachments context
 * changes.  It should be rendered inside ``<PromptInput>`` (or a
 * ``PromptInputProvider``) so the context is available.
 */

import { usePromptInputAttachments } from "@/components/ai-elements/prompt-input";
import { FilePill } from "@/components/ui/file-pill";
import { cn } from "@/lib/utils";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

// ─── Single chip ─────────────────────────────────────────────────────────────

interface AttachmentChipProps {
  id: string;
  filename: string;
  mediaType?: string;
  url?: string;
  onRemove: (id: string) => void;
}

function AttachmentChip({
  id,
  filename,
  mediaType,
  url,
  onRemove,
}: AttachmentChipProps) {
  const { t } = useTranslation();
  return (
    <div data-testid="attachment-chip" data-filename={filename}>
      <FilePill filename={filename} mediaType={mediaType} thumbnailUrl={url}
        onRemove={() => onRemove(id)} removeAriaLabel={t("attachment.remove", { filename })} />
    </div>
  );
}

// ─── Preview list ────────────────────────────────────────────────────────────

export type AttachmentPreviewProps = {
  className?: string;
};

/**
 * Renders the attached-files chip list above the prompt textarea.
 *
 * Renders nothing when there are no attachments — so it can be dropped into
 * the prompt footer unconditionally and stays invisible until the user adds
 * a file.
 */
export function AttachmentPreview({ className }: AttachmentPreviewProps) {
  const { files, remove } = usePromptInputAttachments();

  const handleRemove = useCallback(
    (id: string) => {
      remove(id);
    },
    [remove],
  );

  if (files.length === 0) return null;

  return (
    <div
      className={cn(
        "flex flex-wrap gap-1.5 pb-2",
        className,
      )}
      data-testid="attachment-preview"
    >
      {files.map((f) => (
        <AttachmentChip
          key={f.id}
          id={f.id}
          filename={f.filename ?? "file"}
          mediaType={f.mediaType}
          url={f.url}
          onRemove={handleRemove}
        />
      ))}
    </div>
  );
}
